import {
  Checkbox,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Loader,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { ChefHat, Utensils } from "lucide-react";
import { useEffect, useState } from "react";
import { config } from "@/shared/lib/config";
import { formatDateLocal, formatTime24 } from "@/shared/lib/date-time";
import { fetcher } from "@/shared/lib/fetcher";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import type { Shift } from "../../domain/repo/get-shifts";
import type {
  KitchenTicketStatus,
  KitchenTicketWithDetails,
} from "../../domain/types";
import {
  posKeys,
  useAcknowledgeStockAlert,
  useKitchenStations,
  useKitchenTickets,
  useLatestShift,
  useStockAlerts,
} from "../api/queries";
import { usePOSContext } from "../context/POSContext";
import {
  listLocalKitchenTicketsForBranch,
  OFFLINE_SYNC_CHANGED_EVENT,
} from "../lib/offline-sync/db";
import type { LocalKitchenTicketWithDetails } from "../lib/offline-sync/types";
import { KitchenTicketCard } from "../ui/KitchenTicketCard";
import { StockAlertBanner } from "../ui/StockAlertBanner";

function toKitchenTicketWithDetails(
  localTicket: LocalKitchenTicketWithDetails,
): KitchenTicketWithDetails {
  return {
    ticket: {
      id: localTicket.ticket.localTicketId,
      branchId: localTicket.ticket.branchId,
      orderId: localTicket.ticket.clientOrderId,
      stationId: localTicket.ticket.stationId,
      status: localTicket.ticket.status,
      ticketSequence: localTicket.ticket.ticketSequence,
      note: localTicket.ticket.note ?? null,
      queuedAt: localTicket.ticket.queuedAt,
      startedAt: localTicket.ticket.startedAt ?? null,
      completedAt: localTicket.ticket.completedAt ?? null,
      createdAt: localTicket.ticket.queuedAt,
      updatedAt: localTicket.ticket.updatedAt,
    } as unknown as KitchenTicketWithDetails["ticket"],
    items: localTicket.items.map((item) => ({
      id: item.localTicketItemId,
      kitchenTicketId: item.localTicketId,
      orderItemId: item.localTicketItemId,
      productId: item.productId,
      variantId: item.variantId,
      displayName: item.displayName,
      quantity: item.quantity,
      note: item.note ?? null,
      status: "QUEUED",
      createdAt: item.createdAt,
      updatedAt: item.createdAt,
      variantOptions: item.variantOptions ?? [],
      modifiers: item.modifiers ?? [],
    })) as unknown as KitchenTicketWithDetails["items"],
    order: {
      id: localTicket.ticket.clientOrderId,
      orderNo: localTicket.ticket.clientOrderNo,
      orderType: localTicket.ticket.orderType,
      tableId: localTicket.ticket.tableId ?? null,
      tableName: localTicket.ticket.tableName ?? null,
      customerId: localTicket.ticket.customerId ?? null,
      customerName: null,
    },
    station: {
      id: localTicket.ticket.stationId,
      name: localTicket.ticket.stationName,
      code: localTicket.ticket.stationCode ?? null,
    },
  };
}

export function KitchenDisplayPage() {
  const { selection } = usePOSContext();
  const branchId = selection?.branchId ?? null;

  // Get stock alerts
  const { data: stockAlerts = [] } = useStockAlerts(branchId);
  const acknowledgeAlertMutation = useAcknowledgeStockAlert();

  const [selectedStationId, setSelectedStationId] = useState<
    string | undefined
  >(undefined);
  const [selectedStatus, setSelectedStatus] = useState<
    KitchenTicketStatus | undefined
  >(undefined);
  const [selectedShiftId, setSelectedShiftId] = useState<string>("");

  // Grouping state
  const [groupBy, setGroupBy] = useState<string[]>([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [localTickets, setLocalTickets] = useState<
    LocalKitchenTicketWithDetails[]
  >([]);
  const pageSize = 20;

  // Fetch latest shift for default value
  const { data: latestShift } = useLatestShift(branchId);

  // Set default shift to latest shift when available
  useEffect(() => {
    if (latestShift && !selectedShiftId) {
      setSelectedShiftId(latestShift.id);
    }
  }, [latestShift, selectedShiftId]);

  // Format shift display name
  const formatShiftLabel = (shift: Shift): string => {
    const dateStr = formatDateLocal(shift.openedAt);
    const timeStr = formatTime24(shift.openedAt);
    const statusStr = shift.status === "OPEN" ? " (ເປີດ)" : " (ປິດ)";
    return `${dateStr} ${timeStr}${statusStr}`;
  };

  // Lookup URL for shifts
  const shiftsLookupUrl = branchId
    ? `${config.apiUrl}/pos/shifts/lookup?branchId=${branchId}`
    : "";

  // Wrapper functions for InfiniteCombobox
  const fetchShifts = async ({
    search,
    pageParam,
  }: {
    search: string;
    pageParam: number;
  }): Promise<{
    items: Array<Shift & { name: string }>;
    nextPage: number | null;
  }> => {
    if (!branchId) {
      return { items: [], nextPage: null };
    }
    const limit = 20;
    const page = typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;
    const skip = (page - 1) * limit;

    const url = new URL(shiftsLookupUrl);
    if (search && search.trim().length > 0) {
      url.searchParams.set("q", search);
    }
    url.searchParams.set("limit", String(limit));
    url.searchParams.set("skip", String(skip));

    const data = await fetcher.get<{
      items: Shift[];
      total?: number;
    }>(url.toString());
    const items = Array.isArray(data.items) ? data.items : [];
    const total = typeof data.total === "number" ? data.total : items.length;
    const nextPage = skip + limit < total ? page + 1 : null;

    // Convert shifts to lookup items with name
    return {
      items: items.map((shift) => ({
        ...shift,
        name: formatShiftLabel(shift),
      })),
      nextPage,
    };
  };

  const preloadShift = async (
    id: string,
  ): Promise<(Shift & { name: string }) | null> => {
    if (!branchId || !id) return null;
    const url = `${config.apiUrl}/pos/shifts/lookup/${encodeURIComponent(id)}?branchId=${branchId}`;
    const data = await fetcher.get<{ item?: Shift | null }>(url);
    const shift = data.item;
    if (!shift) return null;
    return { ...shift, name: formatShiftLabel(shift) };
  };

  const { data: stations, isLoading: isLoadingStations } =
    useKitchenStations(branchId);
  const { data: ticketsData, isLoading: isLoadingTickets } = useKitchenTickets(
    branchId,
    {
      stationId: selectedStationId,
      status: selectedStatus,
      shiftId: selectedShiftId || undefined,
      groupBy: groupBy.length > 0 ? groupBy : undefined,
    },
    {
      limit: pageSize,
      offset: (page - 1) * pageSize,
    },
  );

  useEffect(() => {
    if (!branchId) {
      setLocalTickets([]);
      return;
    }

    let cancelled = false;
    const refreshLocalTickets = async () => {
      const next = await listLocalKitchenTicketsForBranch(branchId, {
        shiftId: selectedShiftId || undefined,
        stationId: selectedStationId,
        status: selectedStatus,
      });
      if (!cancelled) setLocalTickets(next);
    };

    void refreshLocalTickets();
    window.addEventListener(OFFLINE_SYNC_CHANGED_EVENT, refreshLocalTickets);
    return () => {
      cancelled = true;
      window.removeEventListener(
        OFFLINE_SYNC_CHANGED_EVENT,
        refreshLocalTickets,
      );
    };
  }, [branchId, selectedShiftId, selectedStationId, selectedStatus]);

  const localTicketsForView = localTickets.map((localTicket) =>
    toKitchenTicketWithDetails(localTicket),
  );
  const tickets = [...localTicketsForView, ...(ticketsData?.data ?? [])];
  const groupedTickets = ticketsData?.grouped ?? [];
  const total = (ticketsData?.total ?? 0) + localTickets.length;
  const totalPages = Math.ceil(total / pageSize);

  const handleGroupByChange = (criteria: string, checked: boolean) => {
    if (checked) {
      setGroupBy((prev) => [...prev, criteria]);
    } else {
      setGroupBy((prev) => prev.filter((c) => c !== criteria));
    }
    setPage(1); // Reset to first page when grouping changes
  };

  if (!branchId) {
    return (
      <div className="container grid h-svh max-w-none items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ChefHat className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີສາຂາ</EmptyTitle>
            <EmptyDescription>ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ກ່ອນ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const handleAcknowledgeAlert = async (alertId: string) => {
    await acknowledgeAlertMutation.mutateAsync(alertId);
  };

  return (
    <div className="container mx-auto max-h-[calc(100vh-100px)] max-w-7xl overflow-y-auto">
      <StockAlertBanner
        alerts={stockAlerts}
        onAcknowledge={handleAcknowledgeAlert}
      />
      <div className="mb-6">
        <h1 className="mb-4 font-bold text-3xl">ຄິວຄົວ (KDS)</h1>

        <div className="flex w-full justify-between">
          <div className="">
            {/* Station Selector */}
            {isLoadingStations ? (
              <Loader />
            ) : (
              <Tabs
                value={selectedStationId ?? "all"}
                onValueChange={(value) => {
                  setSelectedStationId(value === "all" ? undefined : value);
                  setPage(1); // Reset to first page when filter changes
                }}
                className="mb-4"
              >
                <TabsList>
                  <TabsTrigger value="all">ທັງໝົດ</TabsTrigger>
                  {stations?.map((station) => (
                    <TabsTrigger key={station.id} value={station.id}>
                      {station.name}
                    </TabsTrigger>
                  ))}
                  {localTickets.length > 0 && (
                    <TabsTrigger value="LOCAL_UNASSIGNED">Local</TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
            )}

            {/* Status Filter */}
            <Tabs
              value={selectedStatus ?? "all"}
              onValueChange={(value) => {
                setSelectedStatus(
                  value === "all" ? undefined : (value as KitchenTicketStatus),
                );
                setPage(1); // Reset to first page when filter changes
              }}
            >
              <TabsList>
                <TabsTrigger value="all">ທັງໝົດ</TabsTrigger>
                <TabsTrigger value="QUEUED">ລໍຖ້າ</TabsTrigger>
                <TabsTrigger value="IN_PROGRESS">ກຳລັງເຮັດ</TabsTrigger>
                <TabsTrigger value="DONE">ເຮັດແລ້ວ</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Shift Filter */}
          <div className="mb-4">
            <InfiniteCombobox<Shift & { name: string }>
              value={selectedShiftId}
              onValueChange={(value) => {
                setSelectedShiftId(value ?? "");
                setPage(1); // Reset to first page when filter changes
              }}
              queryFn={fetchShifts}
              preloadQueryFn={preloadShift}
              placeholder="ເລືອກ Shift..."
              className="w-full max-w-xs"
              queryKey={[...posKeys.shifts(branchId)]}
              getLabel={(item) => item.name}
              getValue={(item) => item.id}
            />
          </div>
        </div>

        {/* Grouping Options */}
        <div className="mt-4 flex flex-wrap items-center gap-4 rounded-lg border p-3">
          <span className="font-medium text-sm">ຈັດກຸ່ມ:</span>
          <div className="flex items-center gap-2">
            <Checkbox
              id="group-by-table"
              checked={groupBy.includes("same-table")}
              onCheckedChange={(checked) =>
                handleGroupByChange("same-table", checked === true)
              }
            />
            <label
              htmlFor="group-by-table"
              className="font-normal text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              ຈັດກຸ່ມຕາມໂຕະ
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="group-by-customer"
              checked={groupBy.includes("same-customer")}
              onCheckedChange={(checked) =>
                handleGroupByChange("same-customer", checked === true)
              }
            />
            <label
              htmlFor="group-by-customer"
              className="font-normal text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              ຈັດກຸ່ມຕາມລູກຄ້າ
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="group-by-time"
              checked={groupBy.includes("same-time-range")}
              onCheckedChange={(checked) =>
                handleGroupByChange("same-time-range", checked === true)
              }
            />
            <label
              htmlFor="group-by-time"
              className="font-normal text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              ຈັດກຸ່ມຕາມເວລາ
            </label>
          </div>
        </div>
      </div>

      {/* Tickets Grid */}
      {isLoadingTickets ? (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      ) : tickets.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Utensils className="h-8 w-8 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>ບໍ່ມີ Ticket</EmptyTitle>
            <EmptyDescription>ບໍ່ມີ ticket ທີ່ກົງກັບການກອງທີ່ເລືອກ</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {groupBy.length > 0 && groupedTickets.length > 0 ? (
              <>
                {localTicketsForView.map((ticket) => (
                  <KitchenTicketCard key={ticket.ticket.id} ticket={ticket} />
                ))}
                {groupedTickets.map((grouped) => (
                  <KitchenTicketCard key={grouped.groupKey} grouped={grouped} />
                ))}
              </>
            ) : (
              tickets.map((ticket) => (
                <KitchenTicketCard key={ticket.ticket.id} ticket={ticket} />
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className={
                        page === 1
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => setPage(pageNum)}
                              isActive={page === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }
                      if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    },
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      className={
                        page === totalPages
                          ? "pointer-events-none opacity-50"
                          : "cursor-pointer"
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
}
